<script setup lang="ts">
import { CloudOff, Copy, FileCheck2, Images } from 'lucide-vue-next';
import { computed, nextTick, ref, watch } from 'vue';
import { offlineDialog, resolveOfflineConfirmation } from '../composables/offline-dialog';

const cancelButton = ref<HTMLButtonElement>();
const icon = computed(() => ({
  validation: CloudOff,
  survey: FileCheck2,
  evidence: Images,
  overwrite: Copy,
})[offlineDialog.kind]);

watch(() => offlineDialog.open, async (open) => {
  if (!open) return;
  await nextTick();
  cancelButton.value?.focus();
});
</script>

<template>
  <Teleport to="body">
    <Transition name="offline-dialog">
      <div v-if="offlineDialog.open" class="offline-dialog-backdrop" @click.self="resolveOfflineConfirmation(false)" @keydown.esc="resolveOfflineConfirmation(false)">
        <section role="dialog" aria-modal="true" aria-labelledby="offline-dialog-title" aria-describedby="offline-dialog-message">
          <span class="offline-dialog-icon"><component :is="icon" :size="25" /></span>
          <p class="offline-dialog-kicker">Modo sin conexión</p>
          <h2 id="offline-dialog-title">{{ offlineDialog.title }}</h2>
          <p id="offline-dialog-message" class="offline-dialog-message">{{ offlineDialog.message }}</p>
          <div class="offline-dialog-actions">
            <button ref="cancelButton" type="button" @click="resolveOfflineConfirmation(false)">Cancelar</button>
            <button class="offline-dialog-confirm" type="button" @click="resolveOfflineConfirmation(true)">{{ offlineDialog.confirmLabel }}</button>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.offline-dialog-backdrop{position:fixed;inset:0;z-index:2100;padding:calc(20px + env(safe-area-inset-top,0px)) calc(20px + env(safe-area-inset-right,0px)) calc(20px + env(safe-area-inset-bottom,0px)) calc(20px + env(safe-area-inset-left,0px));display:grid;place-items:center;background:rgba(8,12,25,.56);backdrop-filter:blur(5px)}
.offline-dialog-backdrop section{width:min(420px,100%);padding:27px;border-radius:21px;color:#17202a;background:#fff;box-shadow:0 28px 90px rgba(5,9,20,.3)}
.offline-dialog-icon{width:49px;height:49px;display:grid;place-items:center;border-radius:15px;color:#80581f;background:#fff0cf}
.offline-dialog-kicker{margin:17px 0 5px;color:#9a641a;font-size:9px;font-weight:850;letter-spacing:1.15px;text-transform:uppercase}
.offline-dialog-backdrop h2{margin:0;font-family:Georgia,"Times New Roman",serif;font-size:26px;font-weight:500;letter-spacing:-.5px;line-height:1.16}
.offline-dialog-message{margin:11px 0 23px;color:#68716a;font-size:12px;line-height:1.65}
.offline-dialog-actions{display:grid;grid-template-columns:1fr 1fr;gap:9px}
.offline-dialog-actions button{min-height:47px;padding:0 13px;border:1px solid #dfe2e7;border-radius:11px;color:#4d5668;background:#fff;font-size:11px;font-weight:800;cursor:pointer}
.offline-dialog-actions button:focus-visible{outline:2px solid #c18a37;outline-offset:2px}
.offline-dialog-actions .offline-dialog-confirm{color:#fff;border-color:#182038;background:#182038}
.offline-dialog-enter-active,.offline-dialog-leave-active{transition:opacity .17s ease}.offline-dialog-enter-from,.offline-dialog-leave-to{opacity:0}
@media(max-width:520px){.offline-dialog-backdrop{padding:0;place-items:end center}.offline-dialog-backdrop section{padding:24px 19px calc(19px + env(safe-area-inset-bottom,0px));border-radius:21px 21px 0 0}.offline-dialog-actions{grid-template-columns:1fr}.offline-dialog-actions button:first-child{order:2}}
.offline-dialog-actions button{font-size:var(--webapp-action-font-size)}
</style>
